/* eslint-disable max-len */
import { Component, OnInit, EventEmitter, OnChanges, SimpleChanges, Input, Output, ViewChild } from '@angular/core';
import { GridOptions, ColDef, GridReadyEvent, GridSizeChangedEvent } from 'ag-grid-community';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { GroupHeader, MarketplaceOrder, MarketplaceOrderStatus, MarketplaceProductOrdered } from 'src/app/gamification/command-center/models/marketplace-order.model';
import { PointStoreProductOrderService } from 'src/app/gamification/command-center/services/point-store-product-order.service';
import { MarketplaceService } from 'src/app/gamification/command-center/services/marketplace.service';
import { CvNumberLocalizePipe } from '../../../../../../reusables/pipes/cv-number-localize.pipe';
import { DatePipe } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-marketplace-orders-table',
    templateUrl: './marketplace-orders-table.component.html',
    styleUrls: ['./marketplace-orders-table.component.scss']
})
export class MarketplaceOrdersTableComponent implements OnInit, OnChanges {
    @Input() orders: MarketplaceOrder[] = [];
    @Input() search: string;
    @Input() timeInterval: number;
    @Input() editable: boolean;
    @Input() status: any[];
    @Input() headersNames: GroupHeader[] = [];
    @Output() orderUpdated: EventEmitter<{ newValue: MarketplaceOrder, oldValue: MarketplaceOrder }>;
    @ViewChild('marketplaceOrdersTableGrid') agGrid: AgGridAngular;

    pspof = null;
    getDatePosition = null;
    products: any = null;
    originalOrder: MarketplaceOrder = null;
    grid: GridOptions<MarketplaceOrder> = null;
    columnLocalization = new Map();
    availableStatuses = [
        MarketplaceOrderStatus.Processing,
        MarketplaceOrderStatus.InTransit,
        MarketplaceOrderStatus.Shipped,
        MarketplaceOrderStatus.ReadyForPickup,
        MarketplaceOrderStatus.Complete,
        MarketplaceOrderStatus.Cancelled
    ];

    constructor(
        private datePipe: DatePipe,
        private pointStoreProductOrderService: PointStoreProductOrderService,
        private cvLocalizationService: CVLocalizationService,
        private cvNumberLocalizePipe: CvNumberLocalizePipe,
        private agGridHelperService: AgGridHelperService,
        private marketplaceService: MarketplaceService,
    ) {
        this.pspof = pointStoreProductOrderService.data;
        this.getDatePosition = this.pspof.pointStoreProductAdminOrderFilter.datePosition;
    }

    ngOnInit(): void {
        // Load column localization
        this.loadLocalization();
    }

    loadLocalization(): void {
        this.cvLocalizationService.GetSection("Order_Management").then((map) => {
            this.columnLocalization = map;
            this.setupGrid();
        });

        this.cvLocalizationService.GetSection("MarketPlace");
    }

    setupGrid(): void {
        this.grid = this.agGridHelperService.createGridOptions({
            defaultColDef: {
                sortable: true,
                filter: true,
                resizable: true,
                width: 200,
            },

            popupParent: document.querySelector('.ag-menu-option'),

            accentedSort: true,
            animateRows: true,
            masterDetail: true,
            suppressCellFocus: true,
            suppressDragLeaveHidesColumns: true,
            suppressScrollOnNewData: true,
            suppressRowTransform: true,

            columnDefs: [{
                field: 'PointStoreOrderID',
                headerName: this.getColumnName('Order_Number', 'Order Number'),
                cellRenderer: 'agGroupCellRenderer',
                lockVisible: true,
                filter: false,
                minWidth: 200,
            }, {
                field: 'LoginName',
                headerName: this.getColumnName('Ordered_By', 'Ordered By'),
                filter: 'agSetColumnFilter',
                minWidth: 200,
            }, {
                field: 'LoginID',
                headerName: this.getColumnName('Login_ID', 'Agent ID'),
                filter: 'agSetColumnFilter',
                minWidth: 200,
            }, {
                field: 'Group4Name',
                headerName: this.getColumnName('Default', 'Default'),
                filter: 'agSetColumnFilter',
                hide: false,
                minWidth: 200,
            }, {
                field: 'Group3Name',
                headerName: this.getColumnName('Default', 'Default'),
                filter: 'agSetColumnFilter',
                hide: false,
                minWidth: 200,
            }, {
                field: 'Group2Name',
                headerName: this.getColumnName('Default', 'Default'),
                filter: 'agSetColumnFilter',
                hide: false,
                minWidth: 200,
            }, {
                field: 'DateOrdered',
                headerName: this.getColumnName('Date_Ordered', 'Date Ordered'),
                filter: 'agDateColumnFilter',
                cellRenderer: (params) => this.datePipe.transform(params.value, CVAngularDateFormat),
                minWidth: 200,
            }, {
                field: 'OrderTotal',
                headerName: this.getColumnName('Total_Coins', 'Total Coins'),
                filter: 'agNumberColumnFilter',
                type: 'numericColumn',
                cellRenderer: (params) => this.cvNumberLocalizePipe.transform(params.value),
                minWidth: 200,
            }, {
                field: 'OrderStatus',
                headerName: this.getColumnName('Status', 'Status'),
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: {
                    values: this.availableStatuses,
                    formatValue: (status: MarketplaceOrderStatus) => this.getStatusName(status),
                },
                valueFormatter: (params) => this.getStatusName(params.data.OrderStatus),
                editable: this.editable,
                filter: 'agSetColumnFilter',
                minWidth: 200,
            }, {
                field: 'Note',
                headerName: this.getColumnName('Notes', 'Notes'),
                filter: 'agTextColumnFilter',
                cellEditor: 'agLargeTextCellEditor',
                editable: this.editable,
                minWidth: 200,
            }],

            loadingOverlayComponent: this.agGridHelperService.loadingOverlay,

            detailCellRendererParams: {
                detailGridOptions: {
                    defaultColDef: {
                        sortable: true,
                        filter: true,
                        resizable: true
                    },
                    columnDefs: [{
                        field: '_PointStoreProductOrderedDTO.Title',
                        headerName: this.getColumnName('Product', 'Product'),
                        filter: 'agTextColumnFilter',
                        cellRenderer: (params) => {
                            let productContainer = '<div class="d-flex align-items-center h-100">';
                            productContainer += '<img class="mr-3 my-1" style="height: 64px"';

                            let imageId = params.data._PointStoreProductOrderedDTO.ProductImageID > 0 ? params.data._PointStoreProductOrderedDTO.ProductImageID : -15;
                            if (params.data._PointStoreProductOrderedDTO.Variation != null && params.data._PointStoreProductOrderedDTO.Variation > 0 && this.products != null && this.products.length > 0) {
                                let matchedProduct = this.products.find((prod) => prod.PointStoreProductID == params.data._PointStoreProductOrderedDTO.PointStoreProductID);
                                if (matchedProduct.HasMultipleImages && matchedProduct.MultipleImagesData != null) {
                                    let variationImageData = matchedProduct.MultipleImagesData.find((mp) => mp.PointStoreProductVariationID == params.data._PointStoreProductOrderedDTO.Variation);
                                    if (variationImageData != null && variationImageData.PointStoreProductImageID != null && variationImageData.PointStoreProductImageID > 0)
                                        productContainer += ' src="' + window['_BaseURI'] + '/Admin/ImageResizer.aspx?imgid=' + variationImageData.PointStoreProductImageID + '&w=auto&h=165" />';
                                    else
                                        productContainer += ' src="' + window['_BaseURI'] + '/Admin/ImageResizer.aspx?imgid=' + imageId + '&w=auto&h=165" />';
                                } else
                                    productContainer += ' src="' + window['_BaseURI'] + '/Admin/ImageResizer.aspx?imgid=' + imageId + '&w=auto&h=165" />';
                            } else
                                productContainer += ' src="' + window['_BaseURI'] + '/Admin/ImageResizer.aspx?imgid=' + imageId + '&w=auto&h=165" />';

                            // Product name
                            productContainer += '<span>' + params.value + '</span></div>';
                            return productContainer;
                        },
                        minWidth: 300,
                    }, {
                        field: '_PointStoreProductOrderedDTO.Variation',
                        headerName: this.getColumnName('Variation', 'Variation'),
                        filter: 'agTextColumnFilter',
                        cellRenderer: (params) => {
                            if (params.data._PointStoreProductOrderedDTO.Variation != null && params.data._PointStoreProductOrderedDTO.Variation > 0) {
                                if (params.data._PointStoreProductOrderedDTO.VariationName == "" || params.data._PointStoreProductOrderedDTO.VariationName == null)
                                    return '<div class="d-flex align-items-center h-100"><span>N/A</span></div>';
                                else
                                    return '<div class="d-flex align-items-center h-100"><span>' + params.data._PointStoreProductOrderedDTO.VariationName + '</span></div>';
                            }
                            else
                                return '<div class="d-flex align-items-center h-100"><span>N/A</span></div>';
                        },
                        minWidth: 150,
                    }, {
                        field: '_PointStoreProductOrderedDTO.ProductQuantity',
                        headerName: this.getColumnName('Quantity', 'Quantity'),
                        filter: 'agNumberColumnFilter',
                        type: 'numericColumn',
                        minWidth: 150,
                        cellRenderer: (params) => { return '<div class="d-flex align-items-center justify-content-end h-100"><span>' + this.cvNumberLocalizePipe.transform(params.value) + '</span></div>' }
                    }, {
                        field: '_PointStoreProductOrderedDTO.ProductCost',
                        headerName: this.getColumnName('Coins', 'Coins'),
                        filter: 'agNumberColumnFilter',
                        type: 'numericColumn',
                        minWidth: 150,
                        cellRenderer: (params) => { return '<div class="d-flex align-items-center justify-content-end h-100"><span>' + this.cvNumberLocalizePipe.transform(params.value) + '</span></div>' }
                    }, {
                        field: '_PointStoreProductOrderedDTO.ProductStatus',
                        headerName: this.getColumnName('Status', 'Status'),
                        cellEditor: 'agRichSelectCellEditor',
                        cellEditorParams: {
                            values: this.availableStatuses,
                            formatValue: (status: MarketplaceOrderStatus) => this.getStatusName(status),
                        },
                        editable: this.editable,
                        cellRenderer: (params) => { return '<div class="d-flex align-items-center h-100"><span>' + this.getStatusName(params.data._PointStoreProductOrderedDTO.ProductStatus) + '</span></div>' },
                        filter: 'agSetColumnFilter',
                        minWidth: 150
                    }],

                    rowHeight: 74,
                    suppressRowTransform: true,

                    onGridReady: (e: GridReadyEvent) => {
                        e.api.sizeColumnsToFit();
                    },

                    onGridSizeChanged: (e: GridSizeChangedEvent) => {
                        e.api.sizeColumnsToFit();
                    },

                    onCellEditingStarted: (params) => {
                        if (!this.editable)
                            return;

                        let productOrdered = params.data._PointStoreProductOrderedDTO;
                        let order = this.orders.find((o) => o.PointStoreOrderID == productOrdered.PointStoreOrderID);
                        if (!order) {
                            return;
                        }

                        this.originalOrder = angular.copy(order);
                    },

                    onCellEditingStopped: (params) => {
                        if (!this.editable)
                            return;

                        if (params.data._PointStoreProductOrderedDTO.PointStoreOrderID != this.originalOrder.PointStoreOrderID) {
                            return;
                        }

                        // find the order related to this product
                        let productOrdered = params.data._PointStoreProductOrderedDTO;
                        let order = this.orders.find((o) => o.PointStoreOrderID == productOrdered.PointStoreOrderID);
                        order.ProductsCollection.find((p) => p.PointStoreProductID == productOrdered.PointStoreProductID).ProductStatus = productOrdered.ProductStatus;

                        // get lowest product status (sorted by the order in availableStatuses)
                        let ordersSortedByStatus = order.ProductsCollection
                            .map(p => p.ProductStatus)
                            .sort((a, b) => this.availableStatuses.indexOf(a) - this.availableStatuses.indexOf(b));
                        order.OrderStatus = ordersSortedByStatus[0];

                        // check for changes
                        let originalProductOrdered = this.originalOrder.ProductsCollection.find((p) => p.PointStoreProductID == productOrdered.PointStoreProductID);
                        if (order.OrderStatus === this.originalOrder.OrderStatus && originalProductOrdered.ProductStatus === productOrdered.ProductStatus) {
                            // no changes, just exit
                            this.originalOrder = null;
                            return;
                        }

                        // update grid with new order info
                        this.agGrid?.api.applyTransaction({
                            update: [order]
                        });

                        // update on server
                        this.orderUpdated.emit({
                            newValue: order,
                            oldValue: this.originalOrder
                        });

                        this.originalOrder = null;
                    },

                },

                template: (params) => `<div class="h-100"><div class="h-100" ref="eDetailGrid"></div></div>`,

                getDetailRowData: async (params) => {
                    if (params.data.ProductsCollection) {
                        params.successCallback(params.data.ProductsCollection);
                    } else {
                        const tempItem = await this.getDetailRowData(params.data);
                        params.data = tempItem[0];
                        params.successCallback(params.data.ProductsCollection);
                    }

                },
            },

            rowData: [],

            getLocaleText: this.agGridHelperService.getLocaleText,

            isExternalFilterPresent: () => {
                let evalTimeInterval = this.timeInterval == 0 || !!this.timeInterval;
                return !!this.search || evalTimeInterval || !!this.status;
            },

            doesExternalFilterPass: (node) => {
                let item = node.data;
                let match_date = false;
                let match_status = false;

                //match_status = item.OrderStatus in (this.status);
                match_status = this.status.length === 0 || this.status.some(({ value }) => value == item.OrderStatus);

                //node.data
                var position = this.getDatePosition();
                var comparisonDate = new Date();

                if (position == 0 || position == 1) {
                    //30 and 60 days
                    //match_date = (item.DateOrdered >= moment(s).subtract(pointStoreProductOrderFactory.pointStoreProductOrderObj.pointStoreProductAdminOrderFilter.date[position].value, 'days'));
                    match_date = (item.DateOrdered >= comparisonDate.setDate(comparisonDate.getDate() - this.pspof.pointStoreProductAdminOrderFilter.date[position].value));
                } else if (position == 2) {
                    //6 months
                    match_date = (item.DateOrdered >= comparisonDate.setMonth(comparisonDate.getMonth() - 6)); //...OrderFilter.date[position].value for [2] would be 180, which can be off by a few...
                } else if (position >= 3) {
                    match_date = (item.DateOrdered.getFullYear() == this.pspof.pointStoreProductAdminOrderFilter.date[position].value);
                }

                if (match_date && match_status) {
                    // go through each column value and return as soon as we get a match
                    var filterValueLower = this.search.toLowerCase();

                    //-- Empty/Whitespace Input --
                    if (filterValueLower.trim() == '')
                        return true;

                    for (let col of this.agGrid?.api.getColumns()) {
                        var columnValue = this.agGrid?.api.getValue(col, node);
                        if (columnValue && String(columnValue).toLowerCase().indexOf(filterValueLower) >= 0) {
                            return true;
                        }
                    }
                    return false;
                }
                return false;
            },

            getRowId: ({data: order}) => String(order.PointStoreOrderID),

            onGridReady: (e: GridReadyEvent) => {
                e.api.sizeColumnsToFit();
            },

            onGridSizeChanged: (e: GridSizeChangedEvent) => {
                e.api.sizeColumnsToFit();
            },

            onCellEditingStarted: (e) => {
                if (!this.editable)
                    return;

                this.originalOrder = angular.copy(e.data);
            },

            onCellEditingStopped: (e) => {
                if (!this.editable)
                    return;

                let hasChanges = false;

                // check for order status changes
                if (e.column.getColId() === 'OrderStatus' && e.data.OrderStatus !== this.originalOrder.OrderStatus) {
                    // update all products to this status
                    e.data.ProductsCollection.forEach((product: MarketplaceProductOrdered) => {
                        product.ProductStatus = e.data.OrderStatus;
                    });

                    // update on grid
                    e.api.applyTransaction({
                        update: [e.data]
                    });

                    hasChanges = true;
                }

                // check for note changes
                if (e.column.getColId() === "Note" && e.data.Note !== this.originalOrder.Note)
                    hasChanges = true;

                if (!hasChanges) {
                    // no changes made, we're done
                    this.originalOrder = null;
                    return;
                }

                // update on server
                this.orderUpdated.emit({
                    newValue: e.data,
                    oldValue: this.originalOrder
                });

                this.originalOrder = null;
            },
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.orders != undefined && changes.orders.currentValue != null) {
            if (this.agGrid && this.agGrid?.api) {
                this.agGrid?.api.showLoadingOverlay();
                this.agGrid?.api.setGridOption('rowData', changes.orders.currentValue);
            }
        }

        if (changes.search != undefined || changes.timeInterval != undefined || changes.status != undefined) {
            if (this.agGrid && this.agGrid?.api) { // notify filter change
                this.agGrid?.api.onFilterChanged();
            }
        }

        if (changes.headersNames != undefined && changes.headersNames.currentValue != null) {
            if (this.grid && this.agGrid?.api) {
                //get names and visibility for Group Level 2 - 4
                (this.grid.columnDefs[3] as ColDef).hide = changes.headersNames.currentValue[0].HideColumn;
                (this.grid.columnDefs[3] as ColDef).headerName = changes.headersNames.currentValue[0].ColumnName;

                (this.grid.columnDefs[4] as ColDef).hide = changes.headersNames.currentValue[1].HideColumn;
                (this.grid.columnDefs[4] as ColDef).headerName = changes.headersNames.currentValue[1].ColumnName;

                (this.grid.columnDefs[5] as ColDef).hide = changes.headersNames.currentValue[2].HideColumn;
                (this.grid.columnDefs[5] as ColDef).headerName = changes.headersNames.currentValue[2].ColumnName;

                this.agGrid?.api.setGridOption('columnDefs', []);
                this.agGrid?.api.setGridOption('columnDefs', this.grid.columnDefs);
                this.agGrid?.api.autoSizeAllColumns();
                this.agGrid?.api.applyColumnState({ state: [{ colId: 'PointStoreOrderID', sort: 'desc' }] });
            }
        }
    }

    getColumnName(key: string, defaultValue: string): string {
        if (this.columnLocalization && this.columnLocalization.has(key))
            return this.columnLocalization.get(key);
        else
            return defaultValue;
    }

    getStatusName(status: MarketplaceOrderStatus): string {
        // grab localized status name from factory
        let statusInfo = this.pspof.OrderStatus.find((os) => os.value == status);
        if (statusInfo)
            return this.cvLocalizationService.getCachedText("MarketPlace", statusInfo.keyLocalization, statusInfo.name);

        // status not found, fallback to switch
        switch (status) {
            case MarketplaceOrderStatus.InTransit:
                return this.cvLocalizationService.getCachedText("MarketPlace", "Intransit", "In Transit");
            case MarketplaceOrderStatus.ReadyForPickup:
                return this.cvLocalizationService.getCachedText("MarketPlace", "Ready_For_Pickup", "Ready for Pickup");
            case MarketplaceOrderStatus.Complete:
                return this.cvLocalizationService.getCachedText("MarketPlace", "Complete", "Complete");
            case MarketplaceOrderStatus.Shipped:
                return this.cvLocalizationService.getCachedText("MarketPlace", "Shipped", "Shipped");
            case MarketplaceOrderStatus.Cancelled:
                return this.cvLocalizationService.getCachedText("MarketPlace", "Cancelled", "Cancelled");
            case MarketplaceOrderStatus.Created:
            case MarketplaceOrderStatus.Processing:
            default:
                return this.cvLocalizationService.getCachedText("MarketPlace", "Processing", "Processing");
        }
    }

    async getDetailRowData(params): Promise<any[]> {
        let orderData = [];
        orderData.push(params);
        // Load product ordered for selected orders
        await this.marketplaceService.loadProductsOrderedForOrders(orderData).then(() => {
            orderData = <MarketplaceOrder[]>orderData;
        });
        return orderData;
    }
}